import { relevantChangesForContactFields } from "shared/types/Contact";
import { eifStepNavigation } from "shared/types/EIF";
import { relevantChangesForLocationFields } from "shared/types/Location";
import {
  getPlansWhereCanSetClaimsCheckLocations,
  relevantChangesForNonClassPlans,
  type Plan,
} from "shared/types/Plan";
import { getIsChangeDetailInfo } from "shared/utils/EIF/changeLog";
import { exhaustiveCheck } from "shared/utils/exhaustiveCheck";
import { assertIsDefined, rejectNullableValues } from "shared/utils/utils";
import type { Bill, BillId, BillPreview } from "shared/types/Bill";
import type {
  DEIFChangeSnapshot,
  ChangeDetailInfo,
  ChangeDetailInfoList,
  ChangeMetadata,
} from "shared/types/Change";
import type { Client, Policy } from "shared/types/Client";
import type { Contact, ContactId } from "shared/types/Contact";
import type {
  EIFCompanyDetailsSubStep,
  EIFOtherContractDetailsSubStep,
  EIFPlanAdministratorsAndBillingSubStepMap,
  EIFPlanConfigEligibilityOnlyPFMLSubStep,
  EIFStepId,
  EIFSubStepId,
} from "shared/types/EIF";
import type { Subsidiary } from "shared/types/Subsidiary";

type StepArgs = {
  eifStepId: EIFStepId;
  changeSnapshot: DEIFChangeSnapshot;
  client: Client;
  contacts: Contact[];
  bills: Bill[];
  deletedBills: Bill[];
  subsidiaries: Subsidiary[];
};

export function getChangeDetailInfoListForStep(args: StepArgs) {
  const { eifStepId, changeSnapshot, client, contacts, bills, deletedBills, subsidiaries } = args;

  const subSteps = eifStepNavigation[eifStepId];

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const subStepIds = Object.entries(subSteps) as [EIFSubStepId, string][];

  const changeListForCurrentStep = subStepIds.map(
    ([eifSubStepId, subStepName]): [EIFSubStepId, string, ChangeDetailInfo[]] => {
      const list = getChangeDetailInfoListForSubStep({
        eifSubStepId,
        changeSnapshot,
        client,
        contacts,
        bills,
        deletedBills,
        policies: client.policies,
        subsidiaries,
      }).filter(rejectNullableValues);
      const item: [EIFSubStepId, string, ChangeDetailInfo[]] = [eifSubStepId, subStepName, list];
      return item;
    },
  );

  return changeListForCurrentStep;
}

export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: EIFSubStepId;
  client: Client;
  policies: Policy[];
  bills: Bill[];
  deletedBills: Bill[];
  contacts: Contact[];
  subsidiaries: Subsidiary[];
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: "termination-of-insurance";
  client: Client;
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: "rehire-provision";
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: "erisa";
  client: Client;
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: "claims-check-mailing-locations";
  client: Client;
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: "monthly-claims-reports-and-eobs";
  client: Client;
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: "billing-preferences";
  bills: Bill[];
  deletedBills: Bill[];
  policies: Policy[];
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: "claims-payment-reimbursement";
  bills: Bill[];
  policies: Policy[];
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: "plan-administrators";
  contacts: Contact[];
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: "subsidiaries-and-affiliates";
  subsidiaries: Subsidiary[];
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: "non-class-benefits-preferences";
  client: Client;
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: Exclude<
    EIFSubStepId,
    | "termination-of-insurance"
    | "rehire-provision"
    | "erisa"
    | "claims-check-mailing-locations"
    | "monthly-claims-reports-and-eobs"
    | "billing-preferences"
    | "claims-payment-reimbursement"
    | "plan-administrators"
    | "subsidiaries-and-affiliates"
    | "non-class-benefits-preferences"
  >;
}): (ChangeDetailInfo | undefined)[];
export function getChangeDetailInfoListForSubStep(args: {
  changeSnapshot: DEIFChangeSnapshot;
  eifSubStepId: EIFSubStepId;
  client?: Client;
  policies?: Policy[];
  bills?: Bill[];
  deletedBills?: Bill[];
  contacts?: Contact[];
  subsidiaries?: Subsidiary[];
}): (ChangeDetailInfo | undefined)[] {
  const { eifSubStepId, changeSnapshot } = args;

  switch (eifSubStepId) {
    // Company Information
    case "tax-id":
      return [changeSnapshot.Client.taxId];
    case "subsidiaries-and-affiliates": {
      const subsidiaryChanges = Object.values(changeSnapshot.Subsidiary)
        .filter(rejectNullableValues)
        .flatMap((recordWithMetadata) => {
          const { metadata, ...record } = recordWithMetadata;
          return Object.values(record);
        });
      assertIsDefined(args.subsidiaries, "args.subsidiaries");

      const locationIds = args.subsidiaries.map((subsidiary) => subsidiary.location?.id);

      const subsidiaryLocationChanges = Object.entries(changeSnapshot.Location)
        .map(([locationId, location]) => {
          if (!locationIds.includes(locationId)) {
            return null;
          }
          return location;
        })
        .filter(rejectNullableValues)
        .flatMap((recordWithMetadata) => {
          const { metadata, ...record } = recordWithMetadata;
          return Object.values(record);
        });

      return [
        changeSnapshot.Client.hasSubsidiary,
        changeSnapshot.Client.subsidiaryEEsInNYNJOrHI,
        changeSnapshot.Client.mainPolicyHolderEEsInNYNJOrHI,
        changeSnapshot.Client.mainPolicyHolderEEStates,
        ...subsidiaryChanges,
        ...subsidiaryLocationChanges,
      ];
    }
    case "current-sun-life-policies":
      return [
        changeSnapshot.Client.previouslyInsuredBySunLife,
        changeSnapshot.Client.previousSlfPolicies,
      ];
    case "previous-coverage-information":
      return [changeSnapshot.Client.benefitsReplacedWithSlf];

    // Plan Administratin & Billing
    case "plan-administrators": {
      const changedContactIdsForAllContacts = Object.keys(changeSnapshot.Contact);
      assertIsDefined(args.contacts, "args.contacts");
      const adminIds = args.contacts
        .filter((contact) => contact.type === "PRIMARY_WEB_ADMIN" || contact.type === "WEB_ADMIN")
        .map((contact) => contact.id);
      const locationIds = args.contacts
        .filter((contact) => contact.type === "PRIMARY_WEB_ADMIN" || contact.type === "WEB_ADMIN")
        .map((contact) => contact.locationId)
        .filter(rejectNullableValues);

      const contactIds = changedContactIdsForAllContacts.filter((cid) => adminIds.includes(cid));
      const contactChanges = relevantChangesForContactFields(contactIds, changeSnapshot);
      const locationChanges = relevantChangesForLocationFields(locationIds, changeSnapshot);
      const contactLocationChanges = getContactLocationChangeDetailInfoListForContacts(
        adminIds,
        changeSnapshot,
      );
      return [...contactChanges, ...locationChanges, ...contactLocationChanges];
    }
    case "billing-preferences": {
      const { bills, deletedBills, policies, changeSnapshot } = args;
      assertIsDefined(policies, "policies");
      assertIsDefined(bills, "bills");
      assertIsDefined(deletedBills, "deletedBills");

      const policyIds = new Set(policies.map((p) => p.id));

      const allBills = bills.concat(deletedBills);

      const billsInThisPolicy = allBills.filter(
        (b) => b.policyId && policyIds.has(b.policyId) && !b.reimbursement,
      );

      const billsChangeDetailInfoList = billsInThisPolicy.flatMap((bill) => {
        const changes = getChangesForBill(bill, changeSnapshot);
        return changes;
      });

      const billingPreferencesChangeDetailInfoList = policies.flatMap((policy) =>
        [
          changeSnapshot.Policy[policy.id]?.billingAdministrationType,
          changeSnapshot.Policy[policy.id]?.billingStructureType,
          changeSnapshot.Policy[policy.id]?.billingSummaryStatementType,
          changeSnapshot.Policy[policy.id]?.billPayrollCycles,
          changeSnapshot.Policy[policy.id]?.billPayrollCyclesOther,
          changeSnapshot.Policy[policy.id]?.billPayrollCyclesExplanation,
          changeSnapshot.Policy[policy.id]?.billSplitType,
          changeSnapshot.Policy[policy.id]?.advanceOrArrears,

          changeSnapshot.Contact[policy.tpaContact?.id ?? ""]?.tpaFirmName,
          changeSnapshot.Contact[policy.tpaContact?.id ?? ""]?.firstName,
          changeSnapshot.Contact[policy.tpaContact?.id ?? ""]?.lastName,
          changeSnapshot.Contact[policy.tpaContact?.id ?? ""]?.email,
          changeSnapshot.Contact[policy.tpaContact?.id ?? ""]?.phoneNumber,
          changeSnapshot.Contact[policy.tpaContact?.id ?? ""]?.title,
          changeSnapshot.Contact[policy.tpaContact?.id ?? ""]?.address1,
          changeSnapshot.Contact[policy.tpaContact?.id ?? ""]?.address2,
          changeSnapshot.Contact[policy.tpaContact?.id ?? ""]?.city,
          changeSnapshot.Contact[policy.tpaContact?.id ?? ""]?.state,
          changeSnapshot.Contact[policy.tpaContact?.id ?? ""]?.zipCode,

          ...billsChangeDetailInfoList,
        ].filter(rejectNullableValues),
      );

      return billingPreferencesChangeDetailInfoList;
    }
    case "claims-payment-reimbursement": {
      const { bills, policies, changeSnapshot } = args;
      assertIsDefined(bills, "bills");
      assertIsDefined(policies, "policies");

      const policyIds = new Set(policies.map((p) => p.id));

      const billsInThisPolicy = bills.filter(
        (b) => b.policyId && policyIds.has(b.policyId) && b.reimbursement,
      );

      const billsChangeDetailInfoList = billsInThisPolicy
        .flatMap((bill) => {
          return [
            changeSnapshot.Bill[bill.id]?.locationId,
            changeSnapshot.Bill[bill.id]?.contactId,
            changeSnapshot.Bill[bill.id]?.hasDifferentMailingAddress,
            changeSnapshot.Bill[bill.id]?.slfCoverages,
          ];
        })
        .filter((item) => item != null);

      return billsChangeDetailInfoList;
    }
    case "claims-check-mailing-locations": {
      const { client } = args;
      assertIsDefined(client, "client");
      const { changesForRelevantPlans } = getClaimsCheckRelevantPlansAndChanges(
        client.plans,
        client.contacts,
        changeSnapshot,
      );
      return changesForRelevantPlans;
    }

    case "monthly-claims-reports-and-eobs": {
      const list = Object.values(changeSnapshot.MonthlyClaimsReportMailingLocation)
        .filter(rejectNullableValues)
        .flatMap((recordWithMetadata) => {
          const { metadata, ...record } = recordWithMetadata;
          return Object.values(record);
        });
      return list;
    }

    // Plan configuration & Eligibility
    case "class-builder": {
      // Any change to EmployeeClass or EmployeeClassPlan
      const list = Object.values(changeSnapshot.EmployeeClass)
        .concat(Object.values(changeSnapshot.EmployeeClassPlan))
        .filter(rejectNullableValues)
        .flatMap((recordWithMetadata) => {
          const { metadata, ...record } = recordWithMetadata;
          return Object.values(record);
        });
      // add in if client changed from single to multiple classes
      return [...list, changeSnapshot.Client.needsEmployeeClasses];
    }
    case "non-class-benefits-preferences": {
      assertIsDefined(args.client, "args.client");
      const clientRelatedChanges = [
        changeSnapshot.Client.pfmlLeaveYearMethod ?? null,
        changeSnapshot.Client.pfmlFixedYearMMDD ?? null,
        changeSnapshot.Client.pfmlLeaveYearFixedYearOption ?? null,
      ].filter(rejectNullableValues);
      const plansRelatedChanges = relevantChangesForNonClassPlans(
        args.client.plans,
        changeSnapshot,
      );
      return [...clientRelatedChanges, ...plansRelatedChanges];
    }
    case "fli-preferences": {
      return [changeSnapshot.Client.fliCoversAllEmployees, changeSnapshot.Client.fliExcludedGroups];
    }

    // Other Contract Details
    case "confirm-policy-anniversary":
      return [changeSnapshot.Client.policyAnniversaryMonth];
    case "additional-waiting-period-rules":
      return [
        changeSnapshot.Client.timeSpentShouldCountTowardsWaitingPeriod,
        changeSnapshot.Client.timeSpentAsPartTimeShouldCount,
        changeSnapshot.Client.timeSpentAsOtherShouldCount,
        changeSnapshot.Client.timeSpentAsOtherShouldCountText,
        changeSnapshot.Client.waitingPeriodShouldBeWaived,
      ];
    case "rehire-provision":
      return [changeSnapshot.Client.reHireProvisionExists, changeSnapshot.Client.rehireProvision];
    case "union-members-and-domestic-partners":
      return [
        changeSnapshot.Client.unionMembersCovered,
        changeSnapshot.Client.domesticPartnersCovered,
      ];
    case "changes-during-annual-enrollment":
      return [
        changeSnapshot.Client.whenDoYouAdministerYourAEP,
        changeSnapshot.Client.whenDoAEPTakeEffect,
        changeSnapshot.Client.whenDoAEPTakeEffectOther,
        changeSnapshot.Client.whenDecreasesInsuranceTakeEffect,
        changeSnapshot.Client.changesOnlyAllowedDuringAE,
        changeSnapshot.Client.whenDoChangesToBenefitsTakeEffect,
      ];
    case "termination-of-insurance": {
      const { client } = args;
      assertIsDefined(client, "client");
      const changeListForTermination = client.plans
        .map((clientPlan) => changeSnapshot.Plan[clientPlan.id]?.terminationDate)
        .filter((i): i is ChangeDetailInfo => i != null);
      return changeListForTermination;
    }
    case "age-rules":
      return [
        changeSnapshot.Client.ageReductionDecreaseTakeEffectWhen,
        changeSnapshot.Client.ageBandIncreaseTakeEffectWhen,
        changeSnapshot.Client.ageForDependentLifeBenefits,
        changeSnapshot.Client.criticalIllnessDependentAge,
        changeSnapshot.Client.criticalIllnessDependentTobacco,
      ];
    case "other-changes-to-coverage":
      return [changeSnapshot.Client.otherCoverageChangesTakeEffectWhen];
    case "employee-certificate": {
      const certificateSplitConfigChanges = Object.values(changeSnapshot.CertificateSplitConfig)
        .filter(rejectNullableValues)
        .flatMap((recordWithMetadata) => {
          const { metadata, ...record } = recordWithMetadata;
          return Object.values(record);
        });
      return [
        changeSnapshot.Client.shouldCertificatesBeSplitInAnotherWay,
        changeSnapshot.Client.shouldCertificatesBeSplitInAnotherWayText,
        ...certificateSplitConfigChanges,
      ];
    }
    case "section-125":
      return [
        changeSnapshot.Client.hasSection125Plan,
        changeSnapshot.Client.hasSection125Dental,
        changeSnapshot.Client.hasSection125Vision,
        changeSnapshot.Client.hasAdditionalCoverageIncludedIn125Plan,
        changeSnapshot.Client.additonalCoverageIncludedInSection125Plan,
      ];
    case "erisa": {
      const { client } = args;
      const erisaChangeDetailInfoList = [
        changeSnapshot.Client.erisaHasPlan,
        changeSnapshot.Client.erisaPrefersPlanDetails,
        changeSnapshot.Client.erisaPlanType,
        changeSnapshot.Client.erisaPlanTypeOther,
        changeSnapshot.Client.erisaPlanAdminName,
        changeSnapshot.Client.erisaStreetAddress,
        changeSnapshot.Client.erisaCity,
        changeSnapshot.Client.erisaState,
        changeSnapshot.Client.erisaZipCode,
        changeSnapshot.Client.erisaPlanNumber,
        changeSnapshot.Client.erisaPlanNumberOther,
        changeSnapshot.Client.erisaPlanYearEndDate_deprecated,
        changeSnapshot.Client.erisaPlanYearEndDateMMDD,
        changeSnapshot.Client.erisaAdministerOwnPlan,
        changeSnapshot.Client.erisaHasAgentForLegalProcess,
        changeSnapshot.Client.erisaIsLegalAgentSameAsAdmin,
        changeSnapshot.Client.erisaLegalAgentName,
        changeSnapshot.Client.erisaLegalAgentStreetAddress,
        changeSnapshot.Client.erisaLegalAgentCity,
        changeSnapshot.Client.erisaLegalAgentState,
        changeSnapshot.Client.erisaLegalAgentZipCode,
      ];
      assertIsDefined(client, "client");

      // relevant if currently YES or was YES and not changing
      const isTaxIdChangeRelevant =
        (changeSnapshot.Client.erisaPrefersPlanDetails &&
          changeSnapshot.Client.erisaPrefersPlanDetails.currentValue.value === "YES") ||
        (client.erisaPrefersPlanDetails === "YES" &&
          !(
            changeSnapshot.Client.erisaPrefersPlanDetails &&
            changeSnapshot.Client.erisaPrefersPlanDetails.currentValue.value === "NO"
          ));

      /* client.tax id can be set in Company Details and in Other Contract Details section
       *  only include tax id change here if relevant to ERISA (i.e. erisaPrefersPlanDetails)
       */
      if (isTaxIdChangeRelevant) {
        return [...erisaChangeDetailInfoList, changeSnapshot.Client.taxId].filter(
          rejectNullableValues,
        );
      }

      return erisaChangeDetailInfoList.filter(rejectNullableValues);
    }
    case "value-added-services":
      return [changeSnapshot.Client.valueAddedServicesOptions];
    case "malpractice-reimbursements": {
      return [changeSnapshot.Client.malpracticeReimbursementPayableTo];
    }
    default:
      exhaustiveCheck(eifSubStepId);
  }

  return [];
}

export type EIFSubStepsHaveChanges<T extends EIFSubStepId> = {
  [K in T]: boolean;
};

export type EIFSubStepsHaveChangesByStep = {
  "company-information": EIFSubStepsHaveChanges<EIFCompanyDetailsSubStep>;
  "plan-administrators-&-billing": EIFSubStepsHaveChanges<EIFPlanAdministratorsAndBillingSubStepMap>;
  "other-contract-details": EIFSubStepsHaveChanges<EIFOtherContractDetailsSubStep>;
  "plan-configuration-&-eligibility": EIFSubStepsHaveChanges<EIFPlanConfigEligibilityOnlyPFMLSubStep>;
};

export function getEIFSubStepsHaveChanges(
  args: Omit<StepArgs, "eifStepId">,
): EIFSubStepsHaveChangesByStep {
  const subStepsHaveChangesBySteps: EIFSubStepsHaveChangesByStep = {
    "company-information": {
      "current-sun-life-policies": false,
      "previous-coverage-information": false,
      "tax-id": false,
      "subsidiaries-and-affiliates": false,
    },
    "plan-administrators-&-billing": {
      "plan-administrators": false,
      "billing-preferences": false,
      "claims-payment-reimbursement": false,
      "claims-check-mailing-locations": false,
      "malpractice-reimbursements": false,
      "monthly-claims-reports-and-eobs": false,
    },
    "other-contract-details": {
      "confirm-policy-anniversary": false,
      "additional-waiting-period-rules": false,
      "rehire-provision": false,
      "union-members-and-domestic-partners": false,
      "changes-during-annual-enrollment": false,
      "termination-of-insurance": false,
      "age-rules": false,
      "other-changes-to-coverage": false,
      "employee-certificate": false,
      "section-125": false,
      erisa: false,
      "value-added-services": false,
    },
    "plan-configuration-&-eligibility": {
      "class-builder": false,
      "fli-preferences": false,
      "non-class-benefits-preferences": false,
    },
  };

  const stepsToGetSubStepChangesFor: Exclude<EIFStepId, "review-&-submit">[] = [
    "company-information",
    "plan-administrators-&-billing",
    "other-contract-details",
    "plan-configuration-&-eligibility",
  ];

  stepsToGetSubStepChangesFor.forEach((stepId) =>
    setSubStepsHaveChangesForStep(stepId, args, subStepsHaveChangesBySteps),
  );

  return subStepsHaveChangesBySteps;
}

function setSubStepsHaveChangesForStep(
  stepId: Exclude<EIFStepId, "review-&-submit">,
  args: Omit<StepArgs, "eifStepId">,
  subStepsHaveChangesBySteps: EIFSubStepsHaveChangesByStep,
) {
  const { changeSnapshot, client, contacts, bills, deletedBills, subsidiaries } = args;
  const cdli = getChangeDetailInfoListForStep({
    eifStepId: stepId,
    client,
    bills,
    deletedBills,
    changeSnapshot,
    contacts,
    subsidiaries,
  });

  cdli.forEach(([eifSubStepId, _subStepName, changeDetailInfoList]) => {
    switch (stepId) {
      case "company-information":
        subStepsHaveChangesBySteps[stepId][
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          eifSubStepId as EIFCompanyDetailsSubStep
        ] = changeDetailInfoList.length > 0;
        break;
      case "plan-administrators-&-billing":
        subStepsHaveChangesBySteps[stepId][
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          eifSubStepId as EIFPlanAdministratorsAndBillingSubStepMap
        ] = changeDetailInfoList.length > 0;
        break;
      case "plan-configuration-&-eligibility":
        subStepsHaveChangesBySteps[stepId][
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          eifSubStepId as EIFPlanConfigEligibilityOnlyPFMLSubStep
        ] = changeDetailInfoList.length > 0;
        break;
      case "other-contract-details":
        subStepsHaveChangesBySteps[stepId][
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          eifSubStepId as EIFOtherContractDetailsSubStep
        ] = changeDetailInfoList.length > 0;
        break;
      default:
        exhaustiveCheck(stepId);
        break;
    }
  });
}

export function anyChangesForSubSteps<T extends EIFSubStepId>(
  subStepsHaveChanges: EIFSubStepsHaveChanges<T>,
) {
  return Object.values(subStepsHaveChanges).some((val) => val === true);
}

export function getChangeListDetailInfoIsEmpty(changeDetailInfoList: ChangeDetailInfoList) {
  if (changeDetailInfoList.length === 0) {
    return true;
  }
  const isEmpty = changeDetailInfoList.every((cdlInfo) => cdlInfo == null);
  return isEmpty;
}

export const getClaimsCheckRelevantPlansAndChanges = (
  plans: Plan[],
  contacts: Contact[],
  changeSnapshot: DEIFChangeSnapshot,
) => {
  const relevantPlans = getPlansWhereCanSetClaimsCheckLocations(plans);

  const relevantPlanIds = relevantPlans.map((plan) => plan.id);

  const changesForRelevantPlans = relevantPlanIds
    .flatMap((id) => [
      changeSnapshot.Plan[id]?.sendClaimsCheckTo,
      changeSnapshot.Plan[id]?.claimsCheckPayee,
      changeSnapshot.Plan[id]?.planAdminPayeeContactId,
      changeSnapshot.Plan[id]?.someoneElsePayeeContactId,
    ])
    .filter(rejectNullableValues);

  const someoneElsePayeeContactIds = relevantPlanIds
    .flatMap((planId) =>
      plans.map((plan) =>
        plan.id === planId && plan.claimsCheckPayee === "SOMEONE_ELSE"
          ? plan.someoneElsePayeeContactId
          : null,
      ),
    )
    .filter(rejectNullableValues);

  const relevantSomeoneElsePayeeContacts = contacts.filter((contact) =>
    someoneElsePayeeContactIds.includes(contact.id),
  );

  // when the location change a new location is created
  const someoneElsePayeeContactChanges = relevantSomeoneElsePayeeContacts
    .flatMap((contact) => {
      return [
        changeSnapshot.Contact[contact.id]?.firstName,
        changeSnapshot.Contact[contact.id]?.lastName,
        changeSnapshot.Contact[contact.id]?.email,
        changeSnapshot.Contact[contact.id]?.phoneNumber,
        changeSnapshot.Contact[contact.id]?.locationId,
      ];
    })
    .filter(rejectNullableValues);

  const relevantDeletedPlans = Object.keys(changeSnapshot.Plan)
    .filter((key) => changeSnapshot.Plan[key]?.metadata?.claimsCheckRelevant === true)
    .flatMap((key) => [changeSnapshot.Plan[key]?.deletedAt])
    .filter(rejectNullableValues);

  return {
    relevantPlans,
    changesForRelevantPlans: [
      ...changesForRelevantPlans,
      ...relevantDeletedPlans,
      ...someoneElsePayeeContactChanges,
    ],
  };
};

export const getChangesForBill = (bill: BillPreview, changeSnapshot: DEIFChangeSnapshot) => {
  const billLocationChangeDetailInfoList = getBillLocationChangeDetailInfoListForBillSplit(
    [bill.id ?? ""],
    changeSnapshot,
  ).concat(getBillLocationChangeDetailInfoListForSorting([bill.id ?? ""], changeSnapshot));

  const result = new Array<ChangeDetailInfo | ChangeMetadata | undefined>()
    .concat(...Object.values(changeSnapshot.Bill[bill.id ?? ""] ?? {}))
    .concat(...billLocationChangeDetailInfoList)
    .filter(getIsChangeDetailInfo);

  return result;
};

export const getBillLocationChangeDetailInfoListForBillSplit = (
  billIds: BillId[],
  changeSnapshot: DEIFChangeSnapshot,
) => {
  const changeDetailInfoList = Object.entries(changeSnapshot.BillLocation)
    .filter(([billId_locationId]) => billIds.some((billId) => billId_locationId.startsWith(billId)))
    .map(([, changeDetailRecord]) => changeDetailRecord?.billGroupedByLocation)
    .filter((x) => x != null);

  return changeDetailInfoList;
};

export const getBillLocationChangeDetailInfoListForSorting = (
  billIds: BillId[],
  changeSnapshot: DEIFChangeSnapshot,
) => {
  const changeDetailInfoList = Object.entries(changeSnapshot.BillLocation)
    .filter(([billId_locationId]) => billIds.some((billId) => billId_locationId.startsWith(billId)))
    .map(([, changeDetailRecord]) => changeDetailRecord?.category)
    .filter((x) => x != null);

  return changeDetailInfoList;
};

export const getContactLocationChangeDetailInfoListForContacts = (
  contactIds: ContactId[],
  changeSnapshot: DEIFChangeSnapshot,
) => {
  const changeDetailInfoList = Object.entries(changeSnapshot.ContactLocation)
    .filter(([contactId_locationId]) =>
      contactIds.some((contactId) => contactId_locationId.startsWith(contactId)),
    )
    .map(([, changeDetailRecord]) => changeDetailRecord?.contactManagesLocation)
    .filter((x) => x != null);

  return changeDetailInfoList;
};
